// ./src/utils/api/TicketTypeService.ts
//
// Sellable tickets and add-ons for an event. Mirrors the eventsmagic-api
// TicketType SDL. `where.id` accepts one id or many — a bulk update (>1 id) may
// only change status (server-enforced). Base price + limits live here; variable
// pricing (early-bird / volume / …) lives in ticket_price_rule (not managed here yet).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { listAllPages, ListAllOptions } from './listAllPages';
import { Pagination, SortItem } from './commonApiTypes';

/** How/where a ticket is sold (mirrors boundary TICKET_SALES_MODE). */
export const TICKET_SALES_MODE = {
  ON_SALE: 'on_sale',
  DOOR_ONLY: 'door_only',
  OFF: 'off',
} as const;

/** Whether a membership is needed to buy (mirrors boundary TICKET_MEMBERSHIP_MODE). */
export const TICKET_MEMBERSHIP_MODE = {
  ANY: 'any',
  OPTIONAL: 'optional',
  REQUIRED: 'required',
} as const;

/** #228 — a ticket type's own re-entry rule (`settings.reentry`; mirrors the API's TICKET_REENTRY). Absent = the event's. */
export const TICKET_REENTRY = {
  ALLOWED: 'allowed',
  NOT_ALLOWED: 'not_allowed',
} as const;

const FIELDS = `
  id
  eventId
  eventTimeSlotId
  isAddOn
  requiresTicket
  addonCategoryId
  name
  description
  orderNo
  price
  currencyCode
  isPayWhatYouWant
  minPrice
  maxPrice
  membershipMode
  minPerOrder
  maxOrderCount
  orderIncrement
  quantityTotal
  salesStartsAt
  salesEndsAt
  taxSettingId
  salesMode
  settings
  status
  rights
  priceRules { id }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query TicketTypeList($filter: TicketTypeFilter, $params: PaginationAndSorting) {
    ticketTypeList(filter: $filter, params: $params) {
      code
      total
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation TicketTypeCreate($params: TicketTypeInput) {
    ticketTypeCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation TicketTypeUpdate($where: TicketTypeWhereInput, $params: TicketTypeInput) {
    ticketTypeUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation TicketTypeRemove($where: TicketTypeWhereInput) {
    ticketTypeRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface TicketType {
  id?: string;
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  isAddOn?: boolean | null;
  requiresTicket?: boolean | null;
  addonCategoryId?: string | null;
  name?: string | null;
  description?: string | null;
  orderNo?: number | null;
  price?: number | null;
  currencyCode?: string | null;
  isPayWhatYouWant?: boolean | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  membershipMode?: string | null;
  minPerOrder?: number | null;
  maxOrderCount?: number | null;
  orderIncrement?: number | null;
  quantityTotal?: number | null;
  salesStartsAt?: string | null;
  salesEndsAt?: string | null;
  taxSettingId?: string | null;
  salesMode?: string | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
  rights?: string[] | null;
  /** Lightweight list of this ticket's price rules (id only) — for a count badge. */
  priceRules?: { id?: string }[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Mirrors the API's TicketTypeFilter — all narrowing happens server-side. */
export interface TicketTypeFilter {
  id?: string[];
  eventId?: string[];
  eventTimeSlotId?: string[];
  salesMode?: string[];
  isAddOn?: boolean;
  status?: number[];
  /** Free-text over the ticket name (server-side, so it spans every page). */
  searchKeyword?: string;
}

export interface TicketTypeInput {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  isAddOn?: boolean | null;
  requiresTicket?: boolean | null;
  addonCategoryId?: string | null;
  name?: string | null;
  description?: string | null;
  orderNo?: number | null;
  price?: number | null;
  isPayWhatYouWant?: boolean | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  membershipMode?: string | null;
  minPerOrder?: number | null;
  maxOrderCount?: number | null;
  orderIncrement?: number | null;
  quantityTotal?: number | null;
  salesStartsAt?: string | null;
  salesEndsAt?: string | null;
  taxSettingId?: string | null;
  salesMode?: string | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
}

export class TicketTypeService {
  /**
   * One page of an event's ticket types. `total` on the result is the whole
   * matching set — the page derives hasMore from it. Search is server-side
   * (searchKeyword), so it spans every page, not just the loaded one.
   */
  static async list(filter: TicketTypeFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter,
      // No page given is no cap — not a silent 50 (ROADMAP #118; every other service sends none either).
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every ticket type matching the filter, paging through.
   *
   * ⚠ Used by the print rail to split ADMISSIONS from ADD-ONS. A missed page there does not look like a
   * missed page: the tickets whose type fell off the end are simply classified wrongly and quietly
   * excluded from the sheet. See `listAllPages`.
   */
  static async listAll(filter: TicketTypeFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<TicketType>(
      (f, pagination, sort) => TicketTypeService.list((f ?? {}) as TicketTypeFilter, pagination, sort),
      filter,
      sortBy,
      opts,
    );
  }

  static async create(params: TicketTypeInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: TicketTypeInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
